import { Box, Typography, IconButton, Tooltip } from '@mui/material'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import { FS_XS, FS_XL, FS_SM, ICON_SM } from '../common/theme'
import TaEvalScored from './TaEvalScored'
import FancyProgressArc from './FancyProgressArc'
import ScoreArc from './ScoreArc'
import CfpSelect from './CfpSelect'
import TaPadlock from './TaPadlock'
import { useAppActions } from '../AppContext'
import { EMPTY_BIDEVAL } from '../types'
import type { Question } from '../types/question'
import { SCORE_TYPE_OPTIONS } from '../common/common'

interface BidQuestionScoringTabProps {
  question: Question
  onScore: () => void
  isLocked: boolean
  onToggleLock: () => void
  // Wipe the score data (bideval). Caller is expected to gate this behind
  // a confirm.
  onClear?: () => void
  // Read-only display mode (e.g. when mounted inside the wizard's
  // score-details modal). Hides the padlock and the score-type
  // dropdown so the user can't mutate anything - they're just here
  // to inspect what makes up the score.
  readOnly?: boolean
  // Optional previous-version question. When provided the scoring
  // breakdown renders inline deltas (pass → pass / fail → pass etc.
  // per checkpoint, count + score deltas per category / group /
  // overall). Renders on the LEFT (older) of the arrow; the primary
  // `question` is the RIGHT-hand current state.
  prevQuestion?: Question | null
}

export default function BidQuestionScoringTab({ question, onScore, isLocked, onToggleLock, onClear, readOnly = false, prevQuestion }: BidQuestionScoringTabProps) {
  const { updateQuestion } = useAppActions()
  const scoreType = (question.bideval as any)?.scoretypestring ?? ''
  // Current (right) score - the end state.
  const scoreInt = (question.bideval as any)?.scoreint as number | undefined
  // Previous (left) score - what the user started from. Undefined in
  // non-compare mode.
  const prevScoreInt = (prevQuestion?.bideval as any)?.scoreint as number | undefined
  const isCompare = !!prevQuestion

  // Persist a score-type change onto the question's bideval. Going via
  // updateQuestion flips the question's `modified` flag so the Save Bid
  // affordance picks it up.
  // Changing the score type invalidates any existing checkpointeval /
  // scoreint / scoringelements - they were computed under the previous
  // rubric. Reset to EMPTY_BIDEVAL and stamp the new type on top so the
  // user can re-run Estimate Score against the right rubric.
  const handleScoreTypeChange = (next: string) => {
    updateQuestion((question as Question).questionsha, {
      bideval: { ...EMPTY_BIDEVAL, scoretypestring: next },
    })
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: FS_SM, px: 0.5 }}>Estimated Score</Typography>
        {!readOnly && <TaPadlock isLocked={isLocked} onToggle={onToggleLock} />}
        {!readOnly && (
          <CfpSelect
            testId="bqs_scoretype_select"
            value={scoreType}
            options={SCORE_TYPE_OPTIONS}
            onChange={handleScoreTypeChange}
            placeholder="Score Type"
            disabled={isLocked}
          />
        )}
        <Box sx={{ flex: 1 }} />
        {isCompare && (
          <>
            <ScoreArc
              value={prevScoreInt}
              tooltip={typeof prevScoreInt === 'number' && prevScoreInt >= 0
                ? `Score ${prevScoreInt}%`
                : 'Not yet scored'}
            />
            <ArrowRightAltIcon sx={{ fontSize: ICON_SM, opacity: 0.5 }} />
          </>
        )}
        <ScoreArc
          value={scoreInt}
          tooltip={typeof scoreInt === 'number' && scoreInt >= 0 ? `Score ${scoreInt}%` : 'Not yet scored'}
        />
        {onClear && !!question.bideval && (
          <Tooltip title="Clear Score">
            <span>
              <IconButton size="small" disabled={isLocked} onClick={onClear} sx={{ p: 0.25 }}>
                <CancelOutlinedIcon sx={{ fontSize: FS_XL }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
      {!question.bideval ? (
        <Typography variant="caption" sx={{ opacity: 0.4, fontSize: FS_SM }}>
          No scoring data yet. Use Estimate Score to generate.
        </Typography>
      ) : (
        <TaEvalScored
          bideval={question.bideval}
          question={question}
          onEval={onScore}
          readOnly={readOnly}
          prevBideval={prevQuestion?.bideval}
        />
      )}
    </Box>
  )
}
